import {
  type TournamentSummary as LegacyTournamentSummary,
  type TournamentDetail as LegacyTournamentDetail,
  type TournamentRegistrationFieldPreference,
  type TournamentUpdateRequest,
} from '@y-sports/api-client';
import type {
  PlatformDashboardStats,
  TournamentListResponse,
  TournamentSummary as LegacyTournamentListItem,
  UserDashboardStats,
  UserTeamReference,
  UserTournamentReference,
} from '@y-sports/api-client/generated-v2/src/models';
import { TournamentsApi } from '@y-sports/api-client/generated-next-management/src/apis/TournamentsApi';
import type {
  TournamentDetail as NextTournamentDetail,
  TournamentListItem as NextTournamentListItem,
  TournamentSummary as NextTournamentSummary,
} from '@y-sports/api-client/generated-next-management/src/models';
import { Configuration as NextManagementConfiguration } from '@y-sports/api-client/generated-next-management/src/runtime';
import { TournamentStatus, UserTournamentReferenceRole } from '@y-sports/api-client/generated-v2/src/models';
import { UserDashboardStatsFromJSON } from '@y-sports/api-client/generated-v2/src/models/UserDashboardStats';
import { getApiConfigNext } from '@y-sports/api-client';
import { TeamDataService, type ManagedTeamMatch } from './TeamDataService';
import { tournamentDay, fromApiTournamentDate } from '@y-sports/shared';
import { localMatchTime } from './localMatchTime';
import type { ResourcePermissions } from './AccessDataService';

export type TournamentMatchmakingMode = 'FULL_TOURNAMENT' | 'EVENT_STYLE';

export type DashboardTournamentReference = UserTournamentReference & { endDate?: Date };
export type DashboardTeamReference = UserTeamReference & { tournamentEndDate?: Date };
export type TournamentUserDashboardStats = Omit<UserDashboardStats, 'tournamentsCreated' | 'teamsRegistered'> & {
  tournamentsCreated?: DashboardTournamentReference[];
  teamsRegistered?: DashboardTeamReference[];
};

export type TournamentOverviewDetail = Omit<LegacyTournamentDetail, 'tournamentStatus'> & {
  tournamentStatus?: TournamentStatus;
} & Pick<
  NextTournamentDetail,
  'country' | 'state' | 'city' | 'latitude' | 'longitude' | 'matchmakingMode'
>;

export type TournamentOverview = Omit<LegacyTournamentSummary, 'details'> & {
  endDate?: Date;
  details?: TournamentOverviewDetail;
  permissions?: ResourcePermissions | null;
};

export type TournamentListItemWithPermissions = LegacyTournamentListItem & {
  endDate?: string | Date;
  permissions?: ResourcePermissions | null;
};

export interface TournamentCreateInput {
  name?: string;
  location?: string;
  country?: string;
  state?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  date?: Date;
  endDate?: Date;
  creator?: string;
  description?: string;
  matchmakingMode?: TournamentMatchmakingMode;
  isInvitationOnly?: boolean;
  invitedEmails?: string[];
  allowedCountries?: string[];
  allowPaymentReceiptUpload?: boolean;
}

export interface TournamentUpdateInput {
  name?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  date?: Date;
  endDate?: Date;
  description?: string;
  locationUrl?: string;
  infoUrl?: string;
  matchmakingMode?: TournamentMatchmakingMode;
  isInvitationOnly?: boolean;
  invitedEmails?: string[];
  registrationFieldPreferences?: TournamentRegistrationFieldPreference[];
  allowedCountries?: string[];
  allowPaymentReceiptUpload?: boolean;
  showTeamsTab?: boolean;
  showScheduleTab?: boolean;
  showResultsTab?: boolean;
  showStructureTab?: boolean;
  showClassificationTab?: boolean;
  tournamentStatus?: TournamentStatus;
}

export interface TournamentRegistrationDocument {
  id: string;
  fileName: string;
}

export interface UserDashboardMatch extends ManagedTeamMatch {
  teamId: string;
  teamName?: string;
  teamStatus?: string;
  categoryName?: string;
  bucket: 'upcoming' | 'past';
}

type TournamentRegistrationWorkspaceStats = {
  registrations?: Array<{
    status?: string | null;
    playerCount?: number | null;
  }>;
};

const getTournamentsApi = async (): Promise<TournamentsApi> => {
  const apiConfig = await getApiConfigNext();
  return new TournamentsApi(
    new NextManagementConfiguration({
      basePath: apiConfig.basePath,
      headers: apiConfig.headers,
    }),
  );
};

const createRequestHeaders = (headers?: HeadersInit, dropContentType = false): Headers => {
  const normalizedHeaders = new Headers(headers);

  if (dropContentType) {
    normalizedHeaders.delete('Content-Type');
    normalizedHeaders.delete('content-type');
  } else if (!normalizedHeaders.has('Content-Type') && !normalizedHeaders.has('content-type')) {
    normalizedHeaders.set('Content-Type', 'application/json');
  }

  return normalizedHeaders;
};

const dedupeBy = <T,>(items: T[], getKey: (item: T) => string | undefined): T[] => {
  const seen = new Set<string>();

  return items.filter((item, index) => {
    const rawKey = getKey(item);
    const normalizedKey = rawKey?.trim();
    const key = normalizedKey && normalizedKey.length > 0 ? normalizedKey : `__index_${index}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const normalizeDashboardTournaments = (tournaments: DashboardTournamentReference[]): DashboardTournamentReference[] => {
  return dedupeBy(
    tournaments.map((tournament) => ({
      ...tournament,
      role: tournament.role || UserTournamentReferenceRole.Creator,
    })),
    (tournament) => tournament.id,
  );
};

const normalizeDashboardTeams = (teams: DashboardTeamReference[]): DashboardTeamReference[] => {
  return dedupeBy(teams, (team) => {
    if (team.teamId && team.tournamentId) {
      return `${team.teamId}:${team.tournamentId}`;
    }

    return team.teamId || team.tournamentId;
  });
};

const dedupeTeamsByTeamId = (teams: UserTeamReference[]): UserTeamReference[] => {
  return dedupeBy(teams, (team) => team.teamId || team.tournamentId);
};

const normalizeTournamentSummary = (
  tournament: NextTournamentListItem,
): TournamentListItemWithPermissions => ({
  id: tournament.id,
  name: tournament.name,
  location: tournament.location,
  date: fromApiTournamentDate(tournament.date),
  endDate: fromApiTournamentDate(tournament.endDate ?? tournament.date),
  status: tournament.status as LegacyTournamentListItem['status'],
  teamsCount: tournament.teamsCount,
  matchesCount: tournament.matchesCount,
  categoriesCount: tournament.categoriesCount,
  description: tournament.description,
  creatorEmail: tournament.creatorEmail,
  isInvitationOnly: tournament.isInvitationOnly,
  latitude: tournament.latitude,
  longitude: tournament.longitude,
  permissions: (tournament as NextTournamentListItem & { permissions?: ResourcePermissions | null }).permissions,
});

const normalizeTournamentDetail = (
  tournament: NextTournamentSummary,
): TournamentOverview => ({
  ...tournament,
  permissions: (tournament as NextTournamentSummary & { permissions?: ResourcePermissions | null }).permissions,
  date: fromApiTournamentDate(tournament.date),
  endDate: fromApiTournamentDate(tournament.endDate ?? tournament.date),
  details: tournament.details
    ? {
        ...tournament.details,
        tournamentStatus: tournament.details.tournamentStatus as TournamentStatus | undefined,
      }
    : undefined,
});

const emptyUserStats = (): UserDashboardStats => ({
  tournamentsCreated: [],
  teamsRegistered: [],
  tournamentsCount: 0,
  teamsCount: 0,
  matchesCount: 0,
});

export const TournamentOverviewDataService = {
  getPlatformDashboardStats: async (): Promise<PlatformDashboardStats> => {
    const api = await getTournamentsApi();
    const apiConfig = await getApiConfigNext();
    const response = await api.getTournaments();
    const tournaments = response.tournaments ?? [];

    const tournamentStats = await Promise.all(
      tournaments
        .filter((tournament) => !!tournament.id)
        .map(async (tournament) => {
          const registrationWorkspacePromise = fetch(
            `${apiConfig.basePath}/tournaments/${tournament.id}/registration-workspace`,
            {
              headers: apiConfig.headers,
            }
          )
            .then(async (workspaceResponse) => {
              if (!workspaceResponse.ok) {
                throw new Error(`Failed to fetch registration workspace (${workspaceResponse.status})`);
              }

              return (await workspaceResponse.json()) as TournamentRegistrationWorkspaceStats;
            })
            .catch(() => ({ registrations: [] } as TournamentRegistrationWorkspaceStats));

          const [statistics, registrationWorkspace] = await Promise.all([
            api.getTournamentStatistics({ id: tournament.id! }).catch(() => undefined),
            registrationWorkspacePromise,
          ]);

          const registrations = registrationWorkspace.registrations ?? [];

          return {
            totalTeams: registrations.filter(registration => registration.status?.toLowerCase() === 'accepted').length,
            totalPlayers: registrations.reduce(
              (sum: number, registration: { playerCount?: number | null }) => sum + (registration.playerCount ?? 0),
              0
            ),
            totalMatches: statistics?.matchStatistics?.totalMatches ?? tournament.matchesCount ?? 0,
            completedMatches: statistics?.matchStatistics?.completedMatches ?? 0,
            totalGoals: statistics?.matchStatistics?.totalGoals ?? 0,
          };
        })
    );

    return {
      totalTournaments: tournaments.length,
      totalTeams: tournamentStats.reduce((sum, tournament) => sum + tournament.totalTeams, 0),
      totalMatches: tournamentStats.reduce((sum, tournament) => sum + tournament.totalMatches, 0),
      totalPlayers: tournamentStats.reduce((sum, tournament) => sum + tournament.totalPlayers, 0),
      totalGoals: tournamentStats.reduce((sum, tournament) => sum + tournament.totalGoals, 0),
      completedMatches: tournamentStats.reduce((sum, tournament) => sum + tournament.completedMatches, 0),
      activeTournaments: tournaments.filter((tournament) => tournament.status === 'IN_PROGRESS').length,
      upcomingTournaments: tournaments.filter((tournament) =>
        tournament.status === 'SCHEDULED' || tournament.status === 'REGISTRATION' || tournament.status === 'PREPARATION'
      ).length,
    };
  },

  getTournamentsList: async (): Promise<Omit<TournamentListResponse, 'tournaments'> & { tournaments: TournamentListItemWithPermissions[] }> => {
    const api = await getTournamentsApi();
    const response = await api.getTournaments();
    const tournaments = response.tournaments ?? [];

    return {
      tournaments: tournaments.map(normalizeTournamentSummary),
      pagination: {
        page: 0,
        size: tournaments.length,
        totalElements: tournaments.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      },
    };
  },

  getUserDashboardStats: async (email: string): Promise<TournamentUserDashboardStats> => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      return emptyUserStats();
    }

    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/users/${encodeURIComponent(normalizedEmail)}/dashboard-stats`, {
      headers: apiConfig.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user dashboard stats: ${response.status}`);
    }

    const payload = await response.json();
    const stats = UserDashboardStatsFromJSON(payload);
    // The legacy decoder drops new fields and parses calendar dates as UTC.
    const localDay = (value?: string): Date | undefined => value ? new Date(`${value.slice(0, 10)}T00:00:00`) : undefined;
    const tournaments = (stats.tournamentsCreated ?? []).map((tournament, index) => {
      const raw = payload.tournaments_created?.[index];
      return { ...tournament, date: localDay(raw?.date), endDate: localDay(raw?.endDate ?? raw?.date) };
    });
    const teams = (stats.teamsRegistered ?? []).map((team, index) => {
      const raw = payload.teams_registered?.[index];
      return { ...team, tournamentDate: localDay(raw?.tournament_date), tournamentEndDate: localDay(raw?.tournament_end_date ?? raw?.tournament_date) };
    });

    return {
      ...stats,
      tournamentsCreated: normalizeDashboardTournaments(tournaments),
      teamsRegistered: normalizeDashboardTeams(teams),
    };
  },

  getUserDashboardMatches: async (teams: UserTeamReference[]): Promise<UserDashboardMatch[]> => {
    const uniqueTeams = dedupeTeamsByTeamId(teams)
      .filter((team): team is UserTeamReference & { teamId: string } => !!team.teamId);

    const matchesByTeam = await Promise.all(
      uniqueTeams.map(async (team) => {
        const teamMatches = await TeamDataService.getTeamMatches(team.teamId).catch(() => ({ upcoming: [], past: [] }));

        return [
          ...teamMatches.upcoming.map((match) => ({
            ...localMatchTime(match),
            teamId: team.teamId,
            teamName: team.teamName,
            teamStatus: team.teamStatus,
            categoryName: match.categoryName ?? team.categoryName,
            bucket: 'upcoming' as const,
          })),
          ...teamMatches.past.map((match) => ({
            ...localMatchTime(match),
            teamId: team.teamId,
            teamName: team.teamName,
            teamStatus: team.teamStatus,
            categoryName: match.categoryName ?? team.categoryName,
            bucket: 'past' as const,
          })),
        ];
      })
    );

    const toTimestamp = (match: ManagedTeamMatch) => {
      const dateValue = match.tournamentDate ? Date.parse(match.tournamentDate) : Number.NaN;
      return Number.isNaN(dateValue) ? 0 : dateValue;
    };

    return dedupeBy(matchesByTeam.flat(), (match) => match.matchId)
      .sort((left, right) => {
        if (left.bucket !== right.bucket) {
          return left.bucket === 'upcoming' ? -1 : 1;
        }

        const leftDate = toTimestamp(left);
        const rightDate = toTimestamp(right);

        if (left.bucket === 'upcoming') {
          return leftDate - rightDate;
        }

        return rightDate - leftDate;
      });
  },

  getTournamentById: async (tournamentId: string): Promise<TournamentOverview> => {
    const api = await getTournamentsApi();
    return normalizeTournamentDetail(await api.getTournamentById({ id: tournamentId }));
  },

  createTournament: async (tournamentCreateRequest: TournamentCreateInput): Promise<TournamentOverview> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments`, {
      method: 'POST',
      headers: createRequestHeaders(apiConfig.headers),
      body: JSON.stringify({ ...tournamentCreateRequest, date: tournamentDay(tournamentCreateRequest.date), endDate: tournamentDay(tournamentCreateRequest.endDate) }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create tournament: ${response.status}`);
    }

    return normalizeTournamentDetail((await response.json()) as NextTournamentSummary);
  },

  updateTournament: async (
    tournamentId: string,
    tournamentUpdateRequest: TournamentUpdateInput,
  ): Promise<TournamentOverview> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}`, {
      method: 'PATCH',
      headers: createRequestHeaders(apiConfig.headers),
      body: JSON.stringify({ ...tournamentUpdateRequest, date: tournamentDay(tournamentUpdateRequest.date), endDate: tournamentDay(tournamentUpdateRequest.endDate) }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update tournament: ${response.status}`);
    }

    return normalizeTournamentDetail((await response.json()) as NextTournamentSummary);
  },

  uploadTournamentTermsAndConditions: async (tournamentId: string, file: File): Promise<TournamentOverview> => {
    const apiConfig = await getApiConfigNext();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/registration-documents`, {
      method: 'POST',
      headers: createRequestHeaders(apiConfig.headers, true),
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload terms and conditions: ${response.status}`);
    }

    return normalizeTournamentDetail((await response.json()) as NextTournamentSummary);
  },

  getTournamentRegistrationDocuments: async (tournamentId: string): Promise<TournamentRegistrationDocument[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/registration-documents`, {
      headers: createRequestHeaders(apiConfig.headers),
    });
    if (!response.ok) throw new Error(`Failed to fetch registration documents: ${response.status}`);
    return response.json();
  },

  deleteTournamentRegistrationDocument: async (
    tournamentId: string,
    documentId: string,
  ): Promise<TournamentOverview> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(
      `${apiConfig.basePath}/tournaments/${tournamentId}/registration-documents/${documentId}`,
      { method: 'DELETE', headers: createRequestHeaders(apiConfig.headers) },
    );
    if (!response.ok) throw new Error(`Failed to delete registration document: ${response.status}`);
    return normalizeTournamentDetail((await response.json()) as NextTournamentSummary);
  },

  deleteTournamentTermsAndConditions: async (tournamentId: string): Promise<TournamentOverview> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/terms-and-conditions`, {
      method: 'DELETE',
      headers: createRequestHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete terms and conditions: ${response.status}`);
    }

    return normalizeTournamentDetail((await response.json()) as NextTournamentSummary);
  },

  getTournamentTermsAndConditionsBlob: async (tournamentId: string): Promise<Blob> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/terms-and-conditions`, {
      method: 'GET',
      headers: createRequestHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch terms and conditions: ${response.status}`);
    }

    return response.blob();
  },
};
